import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface UploadedFile {
  filePath: string;
  name: string;
  type: 'pdf' | 'image';
  publicUrl?: string; // Only for images
}

export interface InvoiceDocument {
  id: string;
  order_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  processing_status: 'uploaded' | 'processing' | 'completed' | 'error';
  chatgpt_response: any;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  
  // Pricing
  costPerUnit?: number;
  costPerBox?: number;
  suggestedRetailPrice?: number;
  retailMultiplier?: number;
  
  // Categorization
  categoryId?: string;
  categoryName?: string;
  aiSuggestedCategory?: string;
  
  // Status & Sync
  posStatus: 'found' | 'not-found';
  submitStatus: 'draft' | 'ready' | 'submitted';
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed';
  
  // Metadata
  dataQuality: 'high' | 'medium' | 'low';
  hasCredit: boolean;
  lastModifiedBy?: string;
  createdAt: string;
  updatedAt: string;
  
  // Square Integration
  square_item_id?: string;
  square_variation_id?: string;
  
  // Master Product Data
  masterProduct?: {
    id: string;
    productName: string;
    retailPrice?: number;
    orderRetailPrice?: number;
    productCost?: number;
    categoryId?: string;
    categoryName?: string;
    barcode?: string;
    dataSource: string;
  };
  
  // AI Enhancement
  aiEnhancedName?: string;
}

export interface InvoiceItem {
  id: string;
  invoice_document_id: string;
  product_name: string;
  brand?: string;
  barcode_upc?: string;
  pack_info?: string;
  
  // Legacy fields
  quantity?: number;
  unit_price?: number;
  total_price?: number;
  total_cost?: number;
  cost_per_unit?: number;
  master_case_quantity?: number;
  units_per_box?: number;
  quantity_boxes?: number;
  
  // Enhanced fields
  size_string?: string | null;
  pack_qty?: number | null;
  units_per_case?: number | null;
  unit_size?: number | null;
  uom?: string | null;
  price_case?: number | null;
  price_unit?: number | null;
  extended?: number | null;
  currency?: string | null;
  pricing_basis?: 'per_case' | 'per_unit' | 'both' | 'unknown' | string | null;
  raw_terms?: any;
  normalized?: any | null;
  
  // Matching fields
  matched_product_id?: string;
  match_confidence?: number;
  validation_status: 'pending' | 'approved' | 'rejected' | 'use_invoice' | 'use_received' | 'investigate';
}

export interface Category {
  id: string;
  name: string;
  square_category_id?: string;
}

export interface WorkstationFilters {
  searchQuery: string;
  statusFilter: 'all' | 'ready' | 'needs-fix' | 'blocked';
  categoryFilter: string; // category ID or 'all'
  posFilter: 'all' | 'new' | 'existing';
}

interface WorkstationState {
  // Core Data
  orderId: string;
  products: Product[];
  invoiceItems: InvoiceItem[];
  categories: Category[];
  uploadedFiles: UploadedFile[];
  invoiceDocuments: InvoiceDocument[];
  
  // UI State
  activeView: 'scan' | 'invoice' | 'submit';
  selectedProducts: Set<string>;
  filters: WorkstationFilters;
  
  // Global Settings
  globalMultiplier: number;
  globalCostType: 'unit' | 'box';
  
  // Loading States
  isLoading: boolean;
  isSubmitting: boolean;
  isProcessingAI: boolean;
  
  // Actions - Data Management
  setOrderId: (orderId: string) => void;
  setProducts: (products: Product[]) => void;
  setInvoiceItems: (items: InvoiceItem[]) => void;
  setCategories: (categories: Category[]) => void;
  setUploadedFiles: (files: UploadedFile[]) => void;
  setInvoiceDocuments: (documents: InvoiceDocument[]) => void;
  
  // Actions - Product Operations
  updateProduct: (id: string, updates: Partial<Product>) => void;
  bulkUpdateProducts: (ids: string[], updates: Partial<Product>) => void;
  removeProduct: (id: string) => void;
  
  // Actions - Invoice Operations
  updateInvoiceItem: (id: string, updates: Partial<InvoiceItem>) => void;
  matchProductToInvoice: (productId: string, invoiceItemId: string) => void;
  addUploadedFile: (file: UploadedFile) => void;
  removeUploadedFile: (index: number) => void;
  
  // Actions - UI State
  setActiveView: (view: 'scan' | 'invoice' | 'submit') => void;
  toggleProductSelection: (id: string) => void;
  selectAllProducts: (productIds: string[]) => void;
  clearSelection: () => void;
  
  // Actions - Filters
  setSearchQuery: (query: string) => void;
  setStatusFilter: (filter: WorkstationFilters['statusFilter']) => void;
  setCategoryFilter: (categoryId: string) => void;
  setPosFilter: (filter: WorkstationFilters['posFilter']) => void;
  resetFilters: () => void;
  
  // Actions - Global Settings
  setGlobalMultiplier: (multiplier: number) => void;
  setGlobalCostType: (type: 'unit' | 'box') => void;
  
  // Actions - Loading States
  setLoading: (loading: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
  setProcessingAI: (processing: boolean) => void;
  
  // Computed Values
  getFilteredProducts: () => Product[];
  getReadyProducts: () => Product[];
  getNeedsFixProducts: () => Product[];
  getBlockedProducts: () => Product[];
  getProductStats: () => {
    total: number;
    ready: number;
    needsFix: number;
    blocked: number;
    new: number;
    existing: number;
  };
}

const defaultFilters: WorkstationFilters = {
  searchQuery: '',
  statusFilter: 'all',
  categoryFilter: 'all',
  posFilter: 'all'
};

export const useWorkstationStore = create<WorkstationState>()(
  devtools(
    (set, get) => ({
      // Initial State
      orderId: '',
      products: [],
      invoiceItems: [],
      categories: [],
      uploadedFiles: [],
      invoiceDocuments: [],
      activeView: 'scan',
      selectedProducts: new Set(),
      filters: defaultFilters,
      globalMultiplier: 1.5,
      globalCostType: 'unit',
      isLoading: false,
      isSubmitting: false,
      isProcessingAI: false,

      // Data Management Actions
      setOrderId: (orderId) => set({ orderId }),
      setProducts: (products) => set({ products }),
      setInvoiceItems: (invoiceItems) => set({ invoiceItems }),
      setCategories: (categories) => set({ categories }),
      setUploadedFiles: (uploadedFiles) => set({ uploadedFiles }),
      setInvoiceDocuments: (invoiceDocuments) => set({ invoiceDocuments }),

      // Product Operations
      updateProduct: (id, updates) => set((state) => ({
        products: state.products.map(product =>
          product.id === id ? { ...product, ...updates } : product
        )
      })),

      bulkUpdateProducts: (ids, updates) => set((state) => ({
        products: state.products.map(product =>
          ids.includes(product.id) ? { ...product, ...updates } : product
        )
      })),

      removeProduct: (id) => set((state) => ({
        products: state.products.filter(product => product.id !== id),
        selectedProducts: new Set([...state.selectedProducts].filter(sid => sid !== id))
      })),

      // Invoice Operations
      updateInvoiceItem: (id, updates) => set((state) => ({
        invoiceItems: state.invoiceItems.map(item =>
          item.id === id ? { ...item, ...updates } : item
        )
      })),
      
      matchProductToInvoice: (productId, invoiceItemId) => set((state) => ({
        invoiceItems: state.invoiceItems.map(item =>
          item.id === invoiceItemId 
            ? { ...item, matched_product_id: productId, validation_status: 'approved' as const }
            : item
        )
      })),
      
      addUploadedFile: (file) => set((state) => ({
        uploadedFiles: [...state.uploadedFiles, file]
      })),
      
      removeUploadedFile: (index) => set((state) => ({
        uploadedFiles: state.uploadedFiles.filter((_, i) => i !== index)
      })),

      // UI State Actions
      setActiveView: (activeView) => set({ activeView }),

      toggleProductSelection: (id) => set((state) => {
        const newSelection = new Set(state.selectedProducts);
        if (newSelection.has(id)) {
          newSelection.delete(id);
        } else {
          newSelection.add(id);
        }
        return { selectedProducts: newSelection };
      }),

      selectAllProducts: (productIds) => set({ 
        selectedProducts: new Set(productIds) 
      }),

      clearSelection: () => set({ selectedProducts: new Set() }),

      // Filter Actions
      setSearchQuery: (searchQuery) => set((state) => ({
        filters: { ...state.filters, searchQuery }
      })),

      setStatusFilter: (statusFilter) => set((state) => ({
        filters: { ...state.filters, statusFilter }
      })),

      setCategoryFilter: (categoryFilter) => set((state) => ({
        filters: { ...state.filters, categoryFilter }
      })),

      setPosFilter: (posFilter) => set((state) => ({
        filters: { ...state.filters, posFilter }
      })),

      resetFilters: () => set({ filters: defaultFilters }),

      // Global Settings
      setGlobalMultiplier: (globalMultiplier) => set({ globalMultiplier }),
      setGlobalCostType: (globalCostType) => set({ globalCostType }),

      // Loading States
      setLoading: (isLoading) => set({ isLoading }),
      setSubmitting: (isSubmitting) => set({ isSubmitting }),
      setProcessingAI: (isProcessingAI) => set({ isProcessingAI }),

      // Computed Values
      getFilteredProducts: () => {
        const state = get();
        let filtered = [...state.products];

        // Apply search filter
        if (state.filters.searchQuery) {
          const query = state.filters.searchQuery.toLowerCase();
          filtered = filtered.filter(product =>
            product.name.toLowerCase().includes(query) ||
            product.barcode.includes(query) ||
            product.aiEnhancedName?.toLowerCase().includes(query) ||
            product.masterProduct?.productName.toLowerCase().includes(query)
          );
        }

        // Apply status filter
        if (state.filters.statusFilter !== 'all') {
          filtered = filtered.filter(product => {
            const hasRequiredData = product.costPerUnit && product.categoryId;
            const hasIssues = !product.costPerUnit || !product.categoryId;
            
            switch (state.filters.statusFilter) {
              case 'ready':
                return hasRequiredData;
              case 'needs-fix':
                return hasIssues && (product.costPerUnit || product.categoryId);
              case 'blocked':
                return !product.costPerUnit && !product.categoryId;
              default:
                return true;
            }
          });
        }

        // Apply category filter
        if (state.filters.categoryFilter !== 'all') {
          filtered = filtered.filter(product => 
            product.categoryId === state.filters.categoryFilter
          );
        }

        // Apply POS filter
        if (state.filters.posFilter !== 'all') {
          filtered = filtered.filter(product => {
            return state.filters.posFilter === 'new' 
              ? product.posStatus === 'not-found'
              : product.posStatus === 'found';
          });
        }

        return filtered;
      },

      getReadyProducts: () => {
        return get().products.filter(product => 
          product.costPerUnit && product.categoryId
        );
      },

      getNeedsFixProducts: () => {
        return get().products.filter(product => 
          (!product.costPerUnit || !product.categoryId) && 
          (product.costPerUnit || product.categoryId)
        );
      },

      getBlockedProducts: () => {
        return get().products.filter(product => 
          !product.costPerUnit && !product.categoryId
        );
      },

      getProductStats: () => {
        const products = get().products;
        const ready = products.filter(p => p.costPerUnit && p.categoryId).length;
        const needsFix = products.filter(p => 
          (!p.costPerUnit || !p.categoryId) && (p.costPerUnit || p.categoryId)
        ).length;
        const blocked = products.filter(p => !p.costPerUnit && !p.categoryId).length;
        const newProducts = products.filter(p => p.posStatus === 'not-found').length;
        const existing = products.filter(p => p.posStatus === 'found').length;

        return {
          total: products.length,
          ready,
          needsFix,
          blocked,
          new: newProducts,
          existing
        };
      }
    }),
    { name: 'workstation-store' }
  )
);